import React, { useMemo, useState, useEffect, useRef } from "react";
import defaultLogo from "../../JMIlogo.png";

const JsonProductBrowser = ({
  segment,
  categories = [],
  sources = [],
  productEntries = [],
  selectedCategory = "",
  selectedProduct = null,
  selectedSource = "",
  onSelectCategory,
  onSelectProduct,
  onClearSelection,
  onClearAll,
  onSelectSource,
  onAttributeChange,
  attributeValues = { shape: "", color: "", luster: "", finishType: "" },
}) => {
  const [localSearch, setLocalSearch] = useState("");
  const [customizationDropdownOpen, setCustomizationDropdownOpen] =
    useState(false);
  const customizationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        customizationRef.current &&
        !customizationRef.current.contains(e.target)
      ) {
        setCustomizationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Determine which attributes to show based on segment
  const segUpper = (segment || "").toUpperCase();
  const isGemSegment = segUpper === "GEMS" || segUpper === "GEM";
  const isMachineSegment = segUpper === "MACHINE";
  const isToolSegment = segUpper === "TOOL" || segUpper === "TOOLS";
  const isBrandSegment = segUpper === "BRAND";
  const isDecorSegment = segUpper === "DECOR";

  // Extract finishTypes options from selected product
  const finishTypeOptions = useMemo(() => {
    if (!selectedProduct) return [];
    const ft = selectedProduct.finishTypes || selectedProduct.finishes;
    if (Array.isArray(ft)) return ft;
    if (ft && typeof ft === "object") return Object.keys(ft);
    return [];
  }, [selectedProduct]);

  // Power options for Machine segment (e.g. LIGHT DUTY -> ["1-2(kW)", "2-3(kW)", "3-4(kW)"])
  const powerOptions = useMemo(() => {
    if (!selectedProduct) return [];
    const ft = selectedProduct.finishTypes;
    if (ft && typeof ft === "object" && !Array.isArray(ft)) {
      const currentDuty =
        attributeValues.finishType || finishTypeOptions[0] || "LIGHT DUTY";
      return (
        ft[currentDuty] || ft["LIGHT DUTY"] || ["1-2(kW)", "2-3(kW)", "3-4(kW)"]
      );
    }
    return ["1-2(kW)", "2-3(kW)", "3-4(kW)"];
  }, [selectedProduct, attributeValues.finishType, finishTypeOptions]);

  // Handle options for Tools segment from tool.json specification
  const handleOptions = useMemo(() => {
    if (!selectedProduct) return [];
    const rawSpec = selectedProduct.specification;
    if (Array.isArray(rawSpec)) {
      const flat = rawSpec.flat();
      if (flat.length > 0) return flat;
    } else if (typeof rawSpec === "string" && rawSpec) {
      return [rawSpec];
    }
    return [
      "Without grip",
      "PVC grip",
      "Rubber grip",
      "Plastic grip",
      "Wooden grip",
    ];
  }, [selectedProduct]);

  // Customization options for Brand segment (multiselect)
  const customizationOptions = useMemo(() => {
    if (!selectedProduct) return [];
    if (!isBrandSegment && !selectedProduct.customization) return [];
    const raw = selectedProduct.customization;
    if (Array.isArray(raw)) return raw.flat();
    if (typeof raw === "string" && raw) return [raw];
    return isBrandSegment
      ? [
          "Logo Printing",
          "Foil Stamping",
          "Embossing",
          "Debossing",
          "Custom Insert",
          "Custom Color",
        ]
      : [];
  }, [selectedProduct, isBrandSegment]);

  const selectedCustomizations = useMemo(() => {
    const raw = attributeValues?.customization;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }, [attributeValues?.customization]);

  const handleToggleCustomization = (opt) => {
    const isSelected = selectedCustomizations.includes(opt);
    const next = isSelected
      ? selectedCustomizations.filter((item) => item !== opt)
      : [...selectedCustomizations, opt];
    onAttributeChange?.("customization", next);
  };

  const isAllCustomizationSelected = useMemo(() => {
    return (
      customizationOptions.length > 0 &&
      customizationOptions.every((opt) => selectedCustomizations.includes(opt))
    );
  }, [customizationOptions, selectedCustomizations]);

  const handleToggleSelectAllCustomization = () => {
    if (isAllCustomizationSelected) {
      onAttributeChange?.("customization", []);
    } else {
      onAttributeChange?.("customization", [...customizationOptions]);
    }
  };

  // Attribute dropdowns differ per segment
  const attributeFields = useMemo(() => {
    if (!selectedProduct) return [];
    const colorOpts = selectedProduct.color || selectedProduct.colors;

    if (isGemSegment) {
      return [
        { key: "finishType", label: "Type", options: finishTypeOptions },
        { key: "shape", label: "Shape", options: selectedProduct.shape },
        { key: "color", label: "Color", options: colorOpts },
      ];
    }

    if (isMachineSegment) {
      const fields = [
        {
          key: "finishType",
          label: "Type",
          options: finishTypeOptions.length
            ? finishTypeOptions
            : ["LIGHT DUTY", "COMMERCIAL", "HEAVY DUTY"],
        },
        { key: "specification", label: "Power", options: powerOptions },
      ];
      if (colorOpts && colorOpts.length > 0) {
        fields.push({ key: "color", label: "Color", options: colorOpts });
      }
      return fields;
    }

    if (isToolSegment) {
      const fields = [
        { key: "finishType", label: "Type", options: finishTypeOptions },
        { key: "specification", label: "Handle", options: handleOptions },
      ];
      if (colorOpts && colorOpts.length > 0) {
        fields.push({ key: "color", label: "Color", options: colorOpts });
      }
      return fields;
    }

    if (isBrandSegment) {
      const currentCat = (selectedProduct.category || selectedCategory || "")
        .trim()
        .toUpperCase();
      const rawSpec = selectedProduct.specification;
      let specOptions = [];
      if (Array.isArray(rawSpec)) {
        specOptions = rawSpec.flat();
      } else if (typeof rawSpec === "string" && rawSpec) {
        specOptions = [rawSpec];
      }

      // "in case of 'BOX','POUCH','TAG' it will be shown closure but in 'BAG' it will be handel"
      let specLabel = "Specification";
      if (
        currentCat === "BOX" ||
        currentCat === "POUCH" ||
        currentCat === "TAG"
      ) {
        specLabel = "Closure";
      } else if (currentCat === "BAG") {
        specLabel = "Handle";
      }

      const fields = [
        { key: "finishType", label: "Type", options: finishTypeOptions },
      ];

      // "in 'PRINTED' show 'finishing' as finsihing"
      if (
        currentCat === "PRINTED" ||
        (selectedProduct.finishing && Array.isArray(selectedProduct.finishing))
      ) {
        const finishingOpts = Array.isArray(selectedProduct.finishing)
          ? selectedProduct.finishing
          : [
              "Foil Stamping",
              "Embossing",
              "Debossing",
              "Spot UV",
              "Lamination",
              "Die-Cut",
            ];
        fields.push({
          key: "finishing",
          label: "Finishing",
          options: finishingOpts,
        });
      }

      if (specOptions.length > 0) {
        fields.push({
          key: "specification",
          label: specLabel,
          options: specOptions,
        });
      }

      if (colorOpts && colorOpts.length > 0) {
        fields.push({ key: "color", label: "Color", options: colorOpts });
      }

      if (
        selectedProduct.inside &&
        Array.isArray(selectedProduct.inside) &&
        selectedProduct.inside.length > 0
      ) {
        fields.push({
          key: "inside",
          label: "Inside",
          options: selectedProduct.inside,
        });
      }

      return fields;
    }

    if (isDecorSegment) {
      const fields = [
        { key: "finishType", label: "Type", options: finishTypeOptions },
      ];
      if (colorOpts && colorOpts.length > 0) {
        fields.push({ key: "color", label: "Color", options: colorOpts });
      }
      if (selectedProduct.shape && selectedProduct.shape.length > 0) {
        fields.push({
          key: "shape",
          label: "Shape",
          options: selectedProduct.shape,
        });
      }
      return fields;
    }

    return [
      { key: "shape", label: "Shape", options: selectedProduct.shape },
      { key: "color", label: "Color", options: colorOpts },
      { key: "luster", label: "Lustre", options: selectedProduct.luster },
    ];
  }, [
    selectedProduct,
    finishTypeOptions,
    powerOptions,
    handleOptions,
    selectedCategory,
    isGemSegment,
    isMachineSegment,
    isToolSegment,
    isBrandSegment,
    isDecorSegment,
  ]);

  // Sync attribute values when selectedProduct changes
  useEffect(() => {
    if (!selectedProduct) return;
    if (!attributeValues || typeof attributeValues !== "object") return;
    attributeFields.forEach((field) => {
      const currentVal = attributeValues[field.key];
      const validOptions = field.options || [];
      if (
        currentVal == null ||
        currentVal === "" ||
        (validOptions.length > 0 && !validOptions.includes(currentVal))
      ) {
        let defaultValue = "";
        if (field.key === "finishType" && finishTypeOptions.length > 0) {
          defaultValue = finishTypeOptions[0];
        } else if (validOptions.length > 0) {
          defaultValue = validOptions[0];
        } else if (field.key === "finishing" && selectedProduct.finishing) {
          const opts = selectedProduct.finishing;
          defaultValue = Array.isArray(opts) ? opts[0] : opts;
        } else if (selectedProduct[field.key]) {
          const opts = selectedProduct[field.key];
          defaultValue = Array.isArray(opts) ? opts[0] : opts;
        } else if (field.key === "color" && selectedProduct.colors) {
          const opts = selectedProduct.colors;
          defaultValue = Array.isArray(opts) ? opts[0] : opts;
        }
        if (defaultValue && currentVal !== defaultValue) {
          onAttributeChange?.(field.key, defaultValue);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, finishTypeOptions, attributeFields]);

  const filteredProducts = useMemo(() => {
    const q = localSearch.toLowerCase().trim();
    let results = productEntries;

    // Products are already scoped to the selected source; this keeps entries aligned too
    if (selectedSource) {
      results = results.filter((entry) => {
        const src = entry.source || (entry.productSources || [])[0] || "";
        return !src || src.toLowerCase() === selectedSource.toLowerCase();
      });
    }

    if (!q) return results;
    return results.filter(
      (entry) =>
        entry.name.toLowerCase().includes(q) ||
        (entry.productSources || []).some((ps) => ps.toLowerCase().includes(q)),
    );
  }, [productEntries, localSearch, selectedSource]);

  const clearAll = () => {
    setLocalSearch("");
    onClearAll?.();
  };

  const handleCategorySelect = (cat) => {
    setLocalSearch("");
    onSelectProduct(null);
    onSelectSource?.("");
    onSelectCategory(cat);
  };

  const handleSourceSelect = (src) => {
    if (src === selectedSource) return;
    setLocalSearch("");
    onSelectProduct(null);
    onSelectSource?.(src);
  };

  const handleSearchChange = (e) => setLocalSearch(e.target.value);

  const selectProduct = (entry) => {
    onSelectProduct(entry);
  };

  return (
    <div className="json-product-browser">
      <div
        className="json-product-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h3>Product Browser ({segment})</h3>
        <button
          onClick={clearAll}
          className="json-clear-category"
          style={{
            background: "none",
            border: "1px solid var(--gray-300)",
            padding: "4px 8px",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>

      {/* Category Level - e.g. PEARL > "A" */}
      <div style={{ marginBottom: 8 }}>
        <span
          className="form-label"
          style={{
            fontSize: "11px",
            color: "#64748b",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Category
        </span>
      </div>
      <div
        className="category-chips"
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 16,
          overflowX: "auto",
          paddingBottom: 8,
        }}
      >
        {categories.map((cat) => (
          <div
            key={cat}
            className={`category-chip ${
              selectedCategory === cat ? "active" : ""
            }`}
            style={{
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: selectedCategory === cat ? 600 : 500,
              backgroundColor:
                selectedCategory === cat
                  ? "var(--brand-100)"
                  : "var(--gray-100)",
              color:
                selectedCategory === cat
                  ? "var(--brand-600)"
                  : "var(--color-text-secondary)",
              whiteSpace: "nowrap",
              cursor: "pointer",
              transition: "all 0.2s ease",
              border:
                selectedCategory === cat
                  ? "1px solid var(--brand-400)"
                  : "1px solid transparent",
            }}
            onClick={() => handleCategorySelect(cat)}
          >
            {cat}
          </div>
        ))}
      </div>

      {/* Source Level - e.g. PEARL > A > "BASARA" (from SellerSegment) */}
      {selectedCategory && sources.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <span
              className="form-label"
              style={{
                fontSize: "11px",
                color: "#64748b",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Source
            </span>
          </div>
          <div
            className="source-chips"
            style={{
              display: "flex",
              gap: 6,
              overflowX: "auto",
              paddingBottom: 4,
            }}
          >
            {sources.map((src) => (
              <div
                key={src}
                className={`source-chip ${
                  selectedSource === src ? "active" : ""
                }`}
                style={{
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: selectedSource === src ? 500 : 400,
                  backgroundColor:
                    selectedSource === src
                      ? "var(--accent-100)"
                      : "var(--gray-100)",
                  color:
                    selectedSource === src
                      ? "var(--accent-600)"
                      : "var(--color-text-secondary)",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  marginRight: "6px",
                  border:
                    selectedSource === src
                      ? "1px solid var(--accent-400)"
                      : "1px solid transparent",
                }}
                onClick={() => handleSourceSelect(src)}
              >
                {src}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + Product List - only while browsing (category/source chosen, no product picked yet) */}
      {selectedCategory &&
        (sources.length === 0 || selectedSource) &&
        !selectedProduct && (
          <div className="json-product-search" style={{ marginBottom: 16 }}>
            <label className="form-label">Search Product</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by product name..."
              value={localSearch}
              onChange={handleSearchChange}
            />
          </div>
        )}

      {selectedCategory &&
        (sources.length === 0 || selectedSource) &&
        !selectedProduct && (
          <div
            className="json-product-list"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {filteredProducts.length === 0 ? (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#94a3b8",
                }}
              >
                No products matching your selection.
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {filteredProducts.slice(0, 4).map((entry) => (
                  <div
                    key={entry.name}
                    className="registered-item-card"
                    style={{
                      padding: "10px 14px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                    onClick={() => selectProduct(entry)}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {entry.image ? (
                        <img
                          src={entry.image}
                          alt={entry.name}
                          className="registered-item-img"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.src = defaultLogo;
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            background: "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            color: "#64748b",
                          }}
                        >
                          No Image
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "13px",
                          marginBottom: "2px",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        {entry.name}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                          marginBottom: "2px",
                        }}
                      >
                        {entry.sizingUnit || ""}
                      </div>
                      {entry.productSources &&
                        entry.productSources.length > 0 && (
                          <div style={{ fontSize: "10px", color: "#d97706" }}>
                            {entry.productSources[0]}
                          </div>
                        )}
                    </div>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#059669",
                        fontWeight: 500,
                      }}
                    >
                      Select
                    </span>
                  </div>
                ))}
                {filteredProducts.length > 4 && (
                  <div
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "11px",
                    }}
                  >
                    Showing 4 of {filteredProducts.length} products. Refine
                    search to see more.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {/* Selected Product Card - show only when product is selected */}
      {selectedProduct && (
        <div className="json-selected-product" style={{ marginTop: 16 }}>
          <div
            className="registered-item-card"
            style={{
              position: "relative",
              padding: "12px 16px",
              borderRadius: "8px",
              backgroundColor: "#e8f4f0",
              border: "1px solid #059669",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <button
              type="button"
              onClick={onClearSelection}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                padding: "4px 10px",
                background: "#fff",
                border: "1px solid #059669",
                color: "#059669",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                zIndex: 2,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              Change
            </button>
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "6px",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {selectedProduct.image ? (
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="registered-item-img"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = defaultLogo;
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  No Image
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingRight: "65px" }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  marginBottom: "4px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {selectedProduct.name}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "2px",
                }}
              >
                {selectedProduct.category && (
                  <span>Category: {selectedProduct.category}</span>
                )}
                {(selectedProduct.source || selectedSource) && (
                  <span>
                    {selectedProduct.category ? " • " : ""}
                    Source: {selectedProduct.source || selectedSource}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  marginBottom: "6px",
                }}
              >
                {attributeFields.map(({ key, label, options }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                      minWidth: 0,
                    }}
                  >
                    <label
                      htmlFor={`json-attr-${key}`}
                      className="form-label"
                      style={{
                        fontSize: "10px",
                        color: "#64748b",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {label}
                    </label>
                    <select
                      id={`json-attr-${key}`}
                      className="form-input"
                      style={{
                        width: "100%",
                        fontSize: "11px",
                        padding: "4px 6px",
                        boxSizing: "border-box",
                      }}
                      value={attributeValues[key] || ""}
                      onChange={(e) => onAttributeChange?.(key, e.target.value)}
                      disabled={!options || options.length === 0}
                    >
                      {!options || options.length === 0 ? (
                        <option value="">N/A</option>
                      ) : (
                        options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                ))}

                {/* Customization Multi-Select Dropdown with "Select All" */}
                {customizationOptions.length > 0 && (
                  <div
                    ref={customizationRef}
                    style={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                      minWidth: 0,
                    }}
                  >
                    <label
                      className="form-label"
                      style={{
                        fontSize: "10px",
                        color: "#64748b",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Customization
                    </label>
                    <div
                      onClick={() =>
                        setCustomizationDropdownOpen((prev) => !prev)
                      }
                      className="form-input"
                      style={{
                        width: "100%",
                        fontSize: "11px",
                        padding: "4px 6px",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        minHeight: "26px",
                        border: customizationDropdownOpen
                          ? "1px solid #059669"
                          : "1px solid #cbd5e1",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: selectedCustomizations.length
                            ? "#0f172a"
                            : "#94a3b8",
                          fontSize: "11px",
                        }}
                      >
                        {isAllCustomizationSelected
                          ? `All (${customizationOptions.length})`
                          : selectedCustomizations.length > 0
                          ? selectedCustomizations.length === 1
                            ? selectedCustomizations[0]
                            : `${selectedCustomizations.length} Selected`
                          : "Select..."}
                      </span>
                      <span
                        style={{
                          fontSize: "8px",
                          color: "#64748b",
                          marginLeft: "4px",
                        }}
                      >
                        ▼
                      </span>
                    </div>

                    {/* Popover Menu */}
                    {customizationDropdownOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          marginTop: "3px",
                          backgroundColor: "#fff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 60,
                          maxHeight: "180px",
                          overflowY: "auto",
                          padding: "4px 0",
                          minWidth: "150px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Select All Option */}
                        <div
                          onClick={handleToggleSelectAllCustomization}
                          style={{
                            padding: "6px 10px",
                            fontSize: "11px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            borderBottom: "1px solid #f1f5f9",
                            backgroundColor: isAllCustomizationSelected
                              ? "#ecfdf5"
                              : "#f8fafc",
                            color: isAllCustomizationSelected
                              ? "#047857"
                              : "#0f172a",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isAllCustomizationSelected}
                            onChange={() => {}}
                            style={{ cursor: "pointer" }}
                          />
                          <span>
                            Select All ({customizationOptions.length})
                          </span>
                        </div>

                        {/* Individual Option Items */}
                        {customizationOptions.map((opt) => {
                          const isChecked =
                            selectedCustomizations.includes(opt);
                          return (
                            <div
                              key={opt}
                              onClick={() => handleToggleCustomization(opt)}
                              style={{
                                padding: "5px 10px",
                                fontSize: "11px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                backgroundColor: isChecked
                                  ? "#f0fdf4"
                                  : "transparent",
                                color: isChecked ? "#166534" : "#334155",
                              }}
                              onMouseEnter={(e) => {
                                if (!isChecked)
                                  e.currentTarget.style.backgroundColor =
                                    "#f8fafc";
                              }}
                              onMouseLeave={(e) => {
                                if (!isChecked)
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                style={{ cursor: "pointer" }}
                              />
                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {opt}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Customization Summary Tags */}
              {selectedCustomizations.length > 0 && (
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "10px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#64748b" }}>
                    Customization:
                  </span>
                  {selectedCustomizations.map((c) => (
                    <span
                      key={c}
                      style={{
                        padding: "1px 6px",
                        borderRadius: "10px",
                        backgroundColor: "#d1fae5",
                        color: "#065f46",
                        fontSize: "9px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      {c}
                      <span
                        onClick={() => handleToggleCustomization(c)}
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty state when no category selected */}
      {!selectedCategory && !selectedProduct && (
        <div style={{ padding: "30px", textAlign: "center", color: "#94a3b8" }}>
          <div style={{ fontSize: "14px", marginBottom: "8px" }}>
            Select a category to browse products
          </div>
          <div style={{ fontSize: "12px" }}>Category → Source → Product</div>
        </div>
      )}
    </div>
  );
};

export default JsonProductBrowser;
